import { useState, useEffect, useRef, useCallback } from 'react';
import { Product, products } from '../data/products';
import { useTranslatedQuestions } from '../data/questionsTranslated';
import { useAppMode } from '../context/AppModeContext';

type Answers = Record<string, string>;

export interface ProductMatch {
  product: Product;
  matchPercentage: number;
  unmatchedCriteria: string[];
}

export const useRecommendation = () => {
  const translatedQuestions = useTranslatedQuestions();
  const [answers, setAnswers] = useState<Answers>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [productMatches, setProductMatches] = useState<ProductMatch[]>([]);
  const [alternativeProducts, setAlternativeProducts] = useState<Product[]>([]);
  const [unmatchedCriteria, setUnmatchedCriteria] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const handleAnswer = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Move to next question if available
    if (currentQuestionIndex < translatedQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const resetQuiz = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setRecommendedProducts([]);
    setProductMatches([]);
    setAlternativeProducts([]);
    setUnmatchedCriteria([]);
    setIsComplete(false);
  };

  // Find recommended products based on user answers
  useEffect(() => {
    if (isComplete) {
      // Score each product based on matches with user answers
      const scoredProducts = products.map(product => {
        let score = 0;
        let maxPossibleScore = 0;
        let criteriaNotMatched: string[] = [];
        
        // Check each criteria that was answered
        Object.entries(answers).forEach(([questionId, answer]) => {
          maxPossibleScore += 1;
          
          // Check handSize, userType
          if (['handSize', 'userType'].includes(questionId)) {
            if (product.criteria[questionId as keyof typeof product.criteria].includes(answer)) {
              score += 1;
            } else {
              // Add this criteria to unmatched
              const translatedQuestion = translatedQuestions.find(q => q.id === questionId);
              const optionLabel = translatedQuestion?.options.find(o => o.value === answer)?.label || answer;
              criteriaNotMatched.push(`${translatedQuestion?.question || questionId}: ${optionLabel}`);
            }
          }
          
          // Check handedness
          if (questionId === 'handedness') {
            if (product.criteria.handedness === answer || product.criteria.handedness === 'universal') {
              score += 1;
            } else {
              const translatedQuestion = translatedQuestions.find(q => q.id === questionId);
              const optionLabel = translatedQuestion?.options.find(o => o.value === answer)?.label || answer;
              criteriaNotMatched.push(`${translatedQuestion?.question || questionId}: ${optionLabel}`);
            }
          }
        });
        
        // Calculate match percentage
        const matchPercentage = maxPossibleScore > 0 ? (score / maxPossibleScore) * 100 : 0;
        
        return {
          product,
          score,
          matchPercentage,
          unmatchedCriteria: criteriaNotMatched
        };
      });
      
      // Only keep perfect matches (100%)
      const perfectMatches = scoredProducts
        .filter(item => item.matchPercentage === 100)
        .map(item => item.product);
      
      setRecommendedProducts(perfectMatches);
      
      // If no perfect matches, collect the missing criteria to explain why
      if (perfectMatches.length === 0) {
        // Find products with closest match to collect reasons for no match
        const bestMatches = scoredProducts
          .sort((a, b) => b.matchPercentage - a.matchPercentage)
          .slice(0, 3);
        
        setProductMatches(bestMatches);
        
        // Collect all unmatched criteria from best matches
        const allUnmatched = bestMatches.flatMap(match => match.unmatchedCriteria);
        
        // Count frequency of each unmatched criteria
        const criteriaCount = allUnmatched.reduce((acc, criteria) => {
          acc[criteria] = (acc[criteria] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        
        // Get the most common unmatched criteria
        const commonCriteria = Object.entries(criteriaCount)
          .sort((a, b) => b[1] - a[1])
          .map(([criteria]) => criteria);
          
        setUnmatchedCriteria(commonCriteria);
        
        // Find alternative products with same handedness, sorted by hand size (smallest first)
        if (answers.handedness) {
          const handedness = answers.handedness;
          const handSizeOrder = { 'small': 0, 'medium': 1, 'large': 2 };
          
          const alternatives = products
            .filter(p => (p.criteria.handedness === handedness || p.criteria.handedness === 'universal'))
            .sort((a, b) => {
              // Get the first hand size from each product's criteria
              const sizeA = a.criteria.handSize[0] || 'large';
              const sizeB = b.criteria.handSize[0] || 'large';
              
              // Sort by hand size order (small -> medium -> large)
              return (handSizeOrder[sizeA as keyof typeof handSizeOrder] || 2) - 
                     (handSizeOrder[sizeB as keyof typeof handSizeOrder] || 2);
            })
            .slice(0, 3); // Limit to 3 alternatives
            
          setAlternativeProducts(alternatives);
        }
      } else {
        setProductMatches([]);
        setUnmatchedCriteria([]);
        setAlternativeProducts([]);
      }
    }
  }, [isComplete, answers, translatedQuestions]);

  return {
    currentQuestion: translatedQuestions[currentQuestionIndex],
    currentQuestionIndex,
    totalQuestions: translatedQuestions.length,
    handleAnswer,
    goToPreviousQuestion,
    resetQuiz,
    answers,
    recommendedProducts,
    productMatches,
    alternativeProducts,
    unmatchedCriteria,
    isComplete,
    hasPerfectMatch: recommendedProducts.length > 0
  };
};

export const useInactivityReset = (onReset: () => void, enabled: boolean = true) => {
  const { mode } = useAppMode();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    // Only set up auto-reset timer in POS mode when enabled
    if (mode !== 'POS' || !enabled) return;
    
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    timerRef.current = setTimeout(() => {
      // Reset after 20 seconds of inactivity
      onReset();
    }, 20000);
  }, [onReset, mode, enabled]);

  // Set up the initial timer and event listeners
  useEffect(() => {
    // Only set up auto-reset in POS mode when enabled
    if (mode !== 'POS' || !enabled) return;
    
    // Start the timer
    resetTimer();

    // Add event listeners to reset the timer on user activity
    const handleUserActivity = () => resetTimer();
    
    // Add various event listeners to detect user activity
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    // Clean up event listeners and timer when component unmounts
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [resetTimer, mode, enabled]);
}; 